import { useTranslation } from "react-i18next";
import { Row, Col, Card, CardBody, Button, Table } from "react-bootstrap";
import Tab from "react-bootstrap/Tab";
import { useContext, useEffect, useRef, useState } from "react";
import SettingsContext from "@serie3/common/auth/SettingsContext";
import UserAvatar, { useAvatar, invalidateAvatar } from "../../../shared/UserAvatar";
import UserConfig from "@serie3/mymediaplanner/Modules/User/Resources/Endpoints";
import loadSubscriptionData from "@serie3/common/Modules/Payment/Domain/loadSubscriptionData";
import addComma from "@serie3/common/Modules/Payment/Domain/payment";
import crudApi from "@serie3/common/api/crudApi";
import StyledTabs from "../../../shared/Tabs/StyledTabs";
import TabTitle from "../../../shared/Tabs/TabTitle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faTrash, faTriangleExclamation, faCamera } from "@fortawesome/free-solid-svg-icons";

// ── Profile picture ──────────────────────────────────────────────────────────

const MAX_PICTURE_BYTES = 5 * 1024 * 1024;
const PICTURE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

/**
 * Upload / replace / remove the own avatar. The picture is what other group
 * members see next to this user, e.g. in the "publish on the account of"
 * dropdown when they plan a post for this user.
 */
const ProfilePictureCard = () => {
  const { t } = useTranslation();
  const { settings } = useContext(SettingsContext);
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const url = useAvatar({ userId: "me", version });
  const displayName = settings?.user?.name || settings?.user?.username || settings?.email || "";

  const refresh = () => {
    invalidateAvatar("me");
    setVersion((v) => v + 1);
  };

  const onFileChosen = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError(null);

    if (!PICTURE_TYPES.includes(file.type)) {
      setError(t("Backend.Settings.Tabs.Account.PictureTypeError", "Please choose a JPEG, PNG, GIF or WebP image."));
      return;
    }
    if (file.size > MAX_PICTURE_BYTES) {
      setError(t("Backend.Settings.Tabs.Account.PictureSizeError", "The image must be 5 MB or smaller."));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      setBusy(true);
      const response = await crudApi.edit(UserConfig.endpoints.profilePicture.upload, {
        file_string: reader.result,
        file_name: file.name,
      });
      setBusy(false);
      if (response.ok) {
        refresh();
      } else {
        setError(t("Backend.Settings.Tabs.Account.PictureUploadError", "The picture could not be saved."));
      }
    };
    reader.readAsDataURL(file);
  };

  const removePicture = async () => {
    setBusy(true);
    setError(null);
    const response = await crudApi.del(UserConfig.endpoints.profilePicture.remove, "");
    setBusy(false);
    if (response.ok) refresh();
  };

  return (
    <Card>
      <CardBody>
        <h6 style={{ fontWeight: 700, marginBottom: 6, color: "#374151" }}>
          {t("Backend.Settings.Tabs.Account.ProfilePicture", "Profile picture")}
        </h6>
        <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
          {t("Backend.Settings.Tabs.Account.ProfilePictureHint", "Shown to the members of your groups, for example when they plan a post for you.")}
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <UserAvatar userId="me" name={displayName} size={72} version={version} style={{ border: "2px solid #e5e7eb" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <input
              ref={inputRef}
              type="file"
              accept={PICTURE_TYPES.join(",")}
              onChange={onFileChosen}
              style={{ display: "none" }}
            />
            <Button
              variant="outline-primary"
              size="sm"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              style={{ borderRadius: 8, fontSize: 13 }}
            >
              <FontAwesomeIcon icon={faCamera} style={{ marginRight: 6 }} />
              {url
                ? t("Backend.Settings.Tabs.Account.ChangePicture", "Change picture")
                : t("Backend.Settings.Tabs.Account.UploadPicture", "Upload picture")}
            </Button>
            {url && (
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={busy}
                onClick={removePicture}
                style={{ borderRadius: 8, fontSize: 13 }}
              >
                <FontAwesomeIcon icon={faTrash} style={{ marginRight: 6 }} />
                {t("Backend.Settings.Tabs.Account.RemovePicture", "Remove")}
              </Button>
            )}
          </div>
        </div>
        {error && (
          <div style={{ fontSize: 12, color: "#dc2626", marginTop: 10 }}>{error}</div>
        )}
      </CardBody>
    </Card>
  );
};

// ── Account info tab ─────────────────────────────────────────────────────────

const AccountTab = () => {
  const { t } = useTranslation();
  const { settings } = useContext(SettingsContext);

  return (
    <Row className="g-4" style={{ marginTop: 8 }}>
      <Col md={6}>
        <ProfilePictureCard />
      </Col>
      <Col md={6}>
        <Card>
          <CardBody>
            <h6 style={{ fontWeight: 700, marginBottom: 16, color: "#374151" }}>
              {t("Backend.Settings.Tabs.Account.Title", "Account")}
            </h6>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #f3f4f6", paddingBottom: 10 }}>
                <span style={{ fontSize: 13, color: "#6b7280" }}>{t("Login.Email", "Email")}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{settings?.email ?? "—"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #f3f4f6", paddingBottom: 10 }}>
                <span style={{ fontSize: 13, color: "#6b7280" }}>{t("Translation.Title", "Language")}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#111827", textTransform: "uppercase" }}>{settings?.language ?? "en"}</span>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

// ── Billing tab ──────────────────────────────────────────────────────────────

const BillingTab = () => {
  const { t } = useTranslation();
  const [subscriptions, setSubscriptions] = useState([]);

  useEffect(() => {
    loadSubscriptionData(setSubscriptions);
  }, []);

  return (
    <Row style={{ marginTop: 8 }}>
      <Col>
        <Card>
          <CardBody>
            <div className="table-responsive">
              <Table striped>
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>{t("Backend.Settings.Tabs.Payments.Payments.Date")}</th>
                    <th style={{ textAlign: "center" }}>{t("Backend.Settings.Tabs.Payments.Payments.ProductName")}</th>
                    <th style={{ textAlign: "center" }}>{t("Backend.Settings.Tabs.Payments.Payments.Quantity")}</th>
                    <th style={{ textAlign: "center" }}>{t("Backend.Settings.Tabs.Payments.Payments.Price")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((sub, i) => (
                    <tr key={i}>
                      <td style={{ textAlign: "center" }}>{sub.date}</td>
                      <td style={{ textAlign: "center" }}>{sub.product_name}</td>
                      <td style={{ textAlign: "center" }}>1</td>
                      <td style={{ textAlign: "center" }}>{addComma(sub.price)} €</td>
                      <td style={{ textAlign: "center" }}>
                        <Button
                          href="https://billing.stripe.com/p/login/test_bJeeVdaWe2eC4TM7sq83C00"
                          className="w-100"
                          target="_blank"
                        >
                          {t("Backend.Settings.Tabs.Payments.Payments.DownloadButton")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

// ── Delete tab ───────────────────────────────────────────────────────────────

// States:
//  idle            → show "Delete account" button
//  confirm         → show "Are you sure?" prompt
//  emailSent       → requestdelete called, waiting for user to click email link
//  scheduled       → confirmdelete was clicked (from email), deletion date known
//  cancelEmailSent → canceldelete called, waiting for user to click cancel email link

const DeleteTab = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState("idle");     // idle | confirm | emailSent | scheduled | cancelEmailSent
  const [deletionDate, setDeletionDate] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount check if account is already scheduled for deletion
  useEffect(() => {
    const check = async () => {
      const response = await crudApi.justGet(UserConfig.endpoints.checkDeletion);
      if (response.ok && response.data?.deletion_date) {
        setDeletionDate(response.data.deletion_date);
        setStep("scheduled");
      }
      setLoading(false);
    };
    check();
  }, []);

  // Step 1: send confirmation email
  const requestDelete = async () => {
    const response = await crudApi.justGet(UserConfig.endpoints.deleteAccount);
    if (response.ok) setStep("emailSent");
  };

  // Step 3: request cancellation email
  const requestCancelDelete = async () => {
    const response = await crudApi.justGet(UserConfig.endpoints.cancelDelete);
    if (response.ok) setStep("cancelEmailSent");
  };

  // Step 4 (from email link): confirm cancellation
  const confirmCancelDelete = async () => {
    const response = await crudApi.justGet(UserConfig.endpoints.confirmCancelDelete);
    if (response.ok) {
      setDeletionDate(null);
      setStep("idle");
    }
  };

  if (loading) return null;

  return (
    <Row className="g-4" style={{ marginTop: 8 }}>
      <Col md={6}>
        <Card style={{ border: "1px solid #fecaca" }}>
          <CardBody>
            <h6 style={{ fontWeight: 700, marginBottom: 8, color: "#dc2626", display: "flex", alignItems: "center", gap: 8 }}>
              <FontAwesomeIcon icon={faTriangleExclamation} />
              {t("Backend.Settings.Tabs.Account.DangerZone", "Danger Zone")}
            </h6>

            {step === "idle" && (
              <>
                <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
                  {t("Backend.Settings.Tabs.Account.DeleteDescription", "Requesting deletion will send a confirmation email. Your account will only be deleted after you confirm via the link in that email.")}
                </p>
                <Button variant="outline-danger" size="sm" onClick={() => setStep("confirm")} style={{ borderRadius: 8, fontSize: 13 }}>
                  <FontAwesomeIcon icon={faTrash} style={{ marginRight: 6 }} />
                  {t("ProfilePage.DeleteAccountButton", "Delete account")}
                </Button>
              </>
            )}

            {step === "confirm" && (
              <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: 14 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: "#dc2626", marginBottom: 6 }}>
                  {t("Backend.Settings.Tabs.Account.ConfirmDelete", "Are you sure?")}
                </p>
                <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>
                  {t("Backend.Settings.Tabs.Account.ConfirmDeleteHint", "You will receive a confirmation email. Your account is only deleted after clicking the link inside.")}
                </p>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button variant="danger" size="sm" onClick={requestDelete} style={{ borderRadius: 8, fontSize: 13 }}>
                    {t("Backend.Settings.Tabs.Account.SendConfirmEmail", "Send confirmation email")}
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={() => setStep("idle")} style={{ borderRadius: 8, fontSize: 13 }}>
                    {t("Common.Discard")}
                  </Button>
                </div>
              </div>
            )}

            {step === "emailSent" && (
              <div>
                <p style={{ fontSize: 13, color: "#374151", marginBottom: 4 }}>
                  {t("ProfilePage.CheckMailString", "Please check your email account")}
                </p>
                <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
                  {t("Backend.Settings.Tabs.Account.EmailSentHint", "Click the link in the email to confirm deletion. Your account will then be scheduled for removal in 7 days.")}
                </p>
                <Button variant="outline-secondary" size="sm" onClick={() => setStep("idle")} style={{ borderRadius: 8, fontSize: 13 }}>
                  {t("Common.Discard")}
                </Button>
              </div>
            )}

            {step === "scheduled" && (
              <div>
                <p style={{ fontSize: 13, color: "#dc2626", marginBottom: 4, fontWeight: 600 }}>
                  {t("Backend.Settings.Tabs.Account.ScheduledTitle", "Your account is scheduled for deletion.")}
                </p>
                {deletionDate && (
                  <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
                    {t("ProfilePage.DeleteinDaysMessage", "Deletion date:")} <strong>{deletionDate}</strong>
                  </p>
                )}
                <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
                  {t("Backend.Settings.Tabs.Account.CancelHint", "To cancel deletion, click below. You will receive a confirmation email with a cancellation link.")}
                </p>
                <Button variant="outline-secondary" size="sm" onClick={requestCancelDelete} style={{ borderRadius: 8, fontSize: 13 }}>
                  {t("Backend.Settings.Tabs.Account.CancelDeletion", "Cancel deletion")}
                </Button>
              </div>
            )}

            {step === "cancelEmailSent" && (
              <div>
                <p style={{ fontSize: 13, color: "#374151", marginBottom: 4 }}>
                  {t("ProfilePage.CheckMailString", "Please check your email account")}
                </p>
                <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
                  {t("Backend.Settings.Tabs.Account.CancelEmailSentHint", "Click the link in the email to confirm cancellation of your account deletion.")}
                </p>
              </div>
            )}

          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

// ── Root component ───────────────────────────────────────────────────────────

const Account = () => {
  const { t } = useTranslation();

  return (
    <div className="flex-fill">
      <h4 className="mb-4" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <FontAwesomeIcon icon={faUser} style={{ color: "#038edc", fontSize: 18 }} />
        {t("Backend.Settings.Tabs.Account.Title", "Account")}
      </h4>

      <StyledTabs defaultKey="account">
        <Tab
          eventKey="account"
          title={<TabTitle icon="uil-user" label={t("Backend.Settings.Tabs.Account.Title", "Account")} />}
        >
          <AccountTab />
        </Tab>
        <Tab
          eventKey="billing"
          title={<TabTitle icon="uil-invoice" label={t("Backend.Settings.Tabs.Billings", "Billing")} />}
        >
          <BillingTab />
        </Tab>
        <Tab
          eventKey="delete"
          title={<TabTitle icon="uil-trash-alt" label={t("Backend.Settings.Tabs.Account.DeleteTab", "Delete Account")} />}
        >
          <DeleteTab />
        </Tab>
      </StyledTabs>
    </div>
  );
};

export default Account;
